import SiteLayout from '@/components/feature/SiteLayout';
import { useSEO } from '@/hooks/useSEO';
import FaqAccordion from './components/FaqAccordion';
import FeedbackForm from './components/FeedbackForm';

const contactItems = [
  { icon: 'ri-mail-line', label: '客服邮箱', value: 'support@hanmanwebtoon.example.com' },
  { icon: 'ri-question-answer-line', label: '内容合作', value: 'bd@hanmanwebtoon.example.com' },
  { icon: 'ri-time-line', label: '服务时间', value: '每周一至周五 10:00 - 19:00' },
];

export default function AboutPage() {
  useSEO(
    '关于我们-韩漫网址',
    '了解韩漫网址的产品定位、内容说明、版权声明与联系方式，并查看常见问题解答与在线反馈入口。',
    '韩漫网址,关于我们,韩漫平台介绍,版权声明,联系方式',
  );

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-book-2-line text-xl" />
            </span>
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              关于我们
            </span>
          </div>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950 md:text-4xl">关于韩漫网址</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            韩漫网址是一个专注于韩漫漫画在线阅读的资源聚合平台。我们整合精选韩漫作品，按题材细致分类，
            并提供多维筛选、关键词搜索与便捷的章节阅读体验，让每一位韩漫爱好者都能快速找到并沉浸于喜欢的作品。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">我们提供什么</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              韩漫网址围绕「找漫」与「读漫」两个核心场景打磨体验：一方面通过题材分类、地区筛选与排序，
              帮你把海量作品快速收敛到心仪范围；另一方面通过纵向滚动阅读器、夜间模式与阅读宽度调节，
              让长时间阅读也足够舒适。收藏功能让你随时找回追更中的作品。
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                { icon: 'ri-layout-grid-line', title: '丰富分类', desc: '八大题材、多维度筛选，快速定位目标作品。' },
                { icon: 'ri-search-line', title: '全文检索', desc: '支持标题与作者模糊匹配，找漫更高效。' },
                { icon: 'ri-book-read-line', title: '舒适阅读', desc: '纵向滚动、夜间模式、阅读宽度随心调节。' },
                { icon: 'ri-heart-line', title: '本地收藏', desc: '一键收藏追更作品，数据保存在本机浏览器。' },
              ].map((f) => (
                <div
                  key={f.title}
                  className="rounded-lg border border-background-200/80 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${f.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-accent-900">
                <i className="ri-error-warning-line" />
                内容与版权声明
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-accent-900/80">
                本站当前为产品演示版本，页面内所有漫画标题、封面与章节内容均为演示素材，仅用于展示分类浏览与阅读功能，
                <strong>不提供真实的漫画播放与下载服务</strong>。如需正式上线，请确保接入合法授权的内容来源，并遵守相关法律法规。
                所有演示内容版权归原权利人所有。
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-base font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4">
                {contactItems.map((c) => (
                  <li key={c.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                      <i className={`${c.icon} text-base`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="mt-0.5 break-all text-sm text-foreground-800">{c.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-relaxed text-foreground-400">
                以上联系方式为演示信息，实际运营时请替换为真实邮箱与工作时间。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1 rounded-full bg-accent-500" />
            常见问题 FAQ
          </h2>
          <p className="mt-1 text-sm text-foreground-500">关于使用与内容的常见疑问，这里都有答案</p>
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <FaqAccordion />
            <div className="lg:sticky lg:top-24 lg:self-start">
              <FeedbackForm />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}